/**
 * StarRating — reusable 1-to-5 star input + display component.
 *
 * Rate My Pluck (Phase pre-Stripe). Drop-in for both the customer
 * review modal (interactive) and the provider/admin review lists +
 * "Reviewed" pill on past-booking cards (read-only).
 *
 * Props:
 *   value            : number  — current rating (0–5, fractional ok in display mode)
 *   onChange?        : fn(int) — REQUIRED when interactive
 *   size?            : "sm" | "md" | "lg"  (default "md")
 *   readOnly?        : bool    (default false — interactive)
 *   testidPrefix?    : string  — prefix for per-star data-testids
 *                                (defaults to "star-rating")
 *   ariaLabel?       : string  — group aria-label (default "Rating")
 *
 * Visual rules:
 *   - Filled stars use brand orange (#F89623); empty use muted-foreground.
 *   - In `readOnly` display mode, fractional values render as floor()
 *     filled + 1 half-filled (CSS clip) + rest empty so a 4.5 avg
 *     reads cleanly. The aggregate widget passes the raw avg.
 *   - Interactive mode: hover preview, keyboard arrow-key support,
 *     space/enter to commit current focus.
 */
import React, { useState } from "react";
import { Star } from "lucide-react";
import { cn } from "@/lib/utils";

const SIZE_MAP = {
  sm: "h-4 w-4",
  md: "h-6 w-6",
  lg: "h-9 w-9",
};

export default function StarRating({
  value = 0,
  onChange,
  size = "md",
  readOnly = false,
  testidPrefix = "star-rating",
  ariaLabel = "Rating",
}) {
  const [hover, setHover] = useState(0);
  const sizeClass = SIZE_MAP[size] || SIZE_MAP.md;
  const showVal = !readOnly && hover > 0 ? hover : value;

  // Display-mode fractional helper. Only used when readOnly.
  const floor = Math.floor(showVal);
  const frac = readOnly ? Math.max(0, showVal - floor) : 0;

  if (readOnly) {
    return (
      <span
        className="inline-flex items-center gap-0.5"
        role="img"
        aria-label={`${value.toFixed(1)} out of 5 stars`}
        data-testid={`${testidPrefix}-display`}
      >
        {[1, 2, 3, 4, 5].map((i) => {
          const isFull = i <= floor;
          const isHalf = !isFull && i === floor + 1 && frac >= 0.25 && frac < 0.75;
          const isMostlyFull = !isFull && i === floor + 1 && frac >= 0.75;
          return (
            <span key={i} className="relative inline-flex" aria-hidden="true">
              <Star
                className={cn(sizeClass, "text-muted-foreground")}
                strokeWidth={1.75}
              />
              {(isFull || isMostlyFull) && (
                <Star
                  className={cn(
                    sizeClass,
                    "absolute inset-0 text-brand-orange fill-brand-orange"
                  )}
                  strokeWidth={1.75}
                />
              )}
              {isHalf && (
                <span
                  className="absolute inset-0 overflow-hidden"
                  style={{ width: "50%" }}
                >
                  <Star
                    className={cn(sizeClass, "text-brand-orange fill-brand-orange")}
                    strokeWidth={1.75}
                  />
                </span>
              )}
            </span>
          );
        })}
      </span>
    );
  }

  return (
    <div
      className="inline-flex items-center gap-1"
      role="radiogroup"
      aria-label={ariaLabel}
      data-testid={testidPrefix}
    >
      {[1, 2, 3, 4, 5].map((i) => {
        const isFilled = i <= showVal;
        return (
          <button
            key={i}
            type="button"
            role="radio"
            aria-checked={value === i}
            aria-label={`${i} star${i > 1 ? "s" : ""}`}
            className="rounded-md p-1 transition-transform hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange"
            onClick={() => onChange?.(i)}
            onMouseEnter={() => setHover(i)}
            onMouseLeave={() => setHover(0)}
            onFocus={() => setHover(i)}
            onBlur={() => setHover(0)}
            data-testid={`${testidPrefix}-${i}`}
          >
            <Star
              className={cn(
                sizeClass,
                "transition-colors",
                isFilled
                  ? "text-brand-orange fill-brand-orange"
                  : "text-muted-foreground"
              )}
              strokeWidth={1.75}
            />
          </button>
        );
      })}
    </div>
  );
}
